class CartRecommendationsComponent extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.content = null;
    this._abortController = null;
    this.refreshRefs();
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
  }

  refreshRefs() {
    this.container = this.querySelector('.cart-recommendations__container');
    this.content = this.querySelector('.cart-recommendations__content');
  }

  connectedCallback() {
    this.refreshRefs();
    document.addEventListener('cart:update', this.handleCartUpdate);
    
    // Also handle when the drawer opens to refresh recommendations
    const drawer = this.closest('cart-drawer-component');
    if (drawer) {
      const dialog = drawer.querySelector('dialog');
      if (dialog) {
        this.observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
               if (dialog.hasAttribute('open')) {
                  this.fetchRecommendations();
               }
            }
          });
        });
        this.observer.observe(dialog, { attributes: true });
      }
    }
    
    // Initial fetch if cart is not empty (check for .cart-drawer--empty class on parent?)
    // Or just fetch always
    this.fetchRecommendations();
    
    this.addEventListener('submit', this.handleAddToCart);
  }
  
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this._abortController) this._abortController.abort();
    document.removeEventListener('cart:update', this.handleCartUpdate);
  }

  handleCartUpdate(event) {
    // Debounce or just fetch?
    this.fetchRecommendations(event);
  }

  async fetchRecommendations(event) {
    this.refreshRefs();
    this._requestId = (this._requestId || 0) + 1;
    const requestId = this._requestId;

    const productIdStorageKey = 'cart-recommendations:last-product-id';
    const htmlStorageKey = 'cart-recommendations:last-html';

    const cachedHtml = sessionStorage.getItem(htmlStorageKey);
    const isHydratedFromCache =
      !!cachedHtml && !!this.content && (!this.content.innerHTML.trim().length || (this.container && this.container.classList.contains('hidden')));
    if (cachedHtml && this.content) {
      const shouldHydrateFromCache =
        !this.content.innerHTML.trim().length || (this.container && this.container.classList.contains('hidden'));
      if (shouldHydrateFromCache) {
        this.content.innerHTML = cachedHtml;
        if (this.container) this.container.classList.remove('hidden');
      }
    }

    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    try {
      /** @type {any} */
      const shopify = window.Shopify;
      /** @type {any} */
      const eventCart = event?.detail?.resource;

      /** @type {string[]} */
      const productIds = [];

      if (Array.isArray(eventCart?.items) && eventCart.items.length) {
        for (const item of eventCart.items) {
          const productId = item?.product_id ?? item?.productId;
          if (!productId) continue;
          productIds.push(String(productId));
          if (productIds.length >= 4) break;
        }
        if (productIds[0]) sessionStorage.setItem(productIdStorageKey, productIds[0]);
      } else {
        const lastProductId = sessionStorage.getItem(productIdStorageKey);
        if (lastProductId) productIds.push(lastProductId);

        const shouldSkipCartFetch = !!eventCart && productIds.length > 0;
        if (!shouldSkipCartFetch) {
          const cartResponse = await fetch(shopify.routes.root + 'cart.js', { signal });
          const cart = await cartResponse.json();

          if (Array.isArray(cart?.items) && cart.items.length) {
            productIds.length = 0;
            for (const item of cart.items) {
              const productId = item?.product_id;
              if (!productId) continue;
              productIds.push(String(productId));
              if (productIds.length >= 4) break;
            }
            if (productIds[0]) sessionStorage.setItem(productIdStorageKey, productIds[0]);
          }
        }
      }

      if (!productIds.length) {
        if (this.container && !isHydratedFromCache) this.container.classList.add('hidden');
        return;
      }

      const limit = this.dataset.limit || 4;
      for (const productId of productIds) {
        const url = `${this.dataset.url}?section_id=cart-drawer-recommendations&product_id=${encodeURIComponent(
          productId
        )}&limit=${encodeURIComponent(limit)}`;

        const response = await fetch(url, { signal });
        const text = await response.text();
        if (requestId !== this._requestId) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = html.querySelector('.cart-recommendations__grid');

        if (recommendations && recommendations.innerHTML.trim().length) {
          if (this.content) this.content.innerHTML = recommendations.outerHTML;
          if (this.container) this.container.classList.remove('hidden');
          sessionStorage.setItem(productIdStorageKey, productId);
          sessionStorage.setItem(htmlStorageKey, recommendations.outerHTML);
          return;
        }
      }

      if (this.container && !isHydratedFromCache) this.container.classList.add('hidden');
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error('Error fetching recommendations:', e);
      if (this.container && !isHydratedFromCache) this.container.classList.add('hidden');
    }
  }

  /**
   * @param {Event} event
   */
  async handleAddToCart(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.matches('.cart-recommendations-card__form')) return;
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.target);
    const button = form.querySelector('button');
    if (!button) return;
    const originalText = button.textContent;
    
    button.disabled = true;
    button.textContent = '...';

    try {
      const formData = new FormData(form);

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      const sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }
      
      /** @type {any} */
      const theme = typeof Theme !== 'undefined' ? Theme : undefined;
      /** @type {any} */
      const shopify = window.Shopify;
      const cartAddUrl =
        theme?.routes?.cart_add_url || (shopify?.routes?.root ? shopify.routes.root + 'cart/add.js' : '/cart/add.js');

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'text/html',
        },
      });

      const data = await response.json();

      if (response.status !== 200) {
        console.error(data.description);
        button.textContent = 'Error';
        setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
        }, 2000);
      } else {
        button.textContent = 'Added';

        let updatedCount = 0;
        if (data?.sections) {
          try {
            const parser = new DOMParser();
            for (const html of Object.values(data.sections)) {
              if (typeof html !== 'string') continue;
              const doc = parser.parseFromString(html, 'text/html');
              const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent;
              if (countText && countText.trim().length) {
                updatedCount = parseInt(countText, 10) || 0;
                break;
              }
            }
          } catch (_) {
            updatedCount = 0;
          }
        }

        if (!updatedCount && shopify?.routes?.root) {
          try {
            const cartResp = await fetch(shopify.routes.root + 'cart.js');
            const cart = await cartResp.json();
            updatedCount = cart.item_count;
          } catch (_) {
            updatedCount = 0;
          }
        }

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              ...data,
              itemCount: updatedCount,
              sections: data?.sections,
              source: 'cart-recommendations'
            }
          }
        }));
        
        setTimeout(() => {
           button.textContent = originalText;
           button.disabled = false;
        }, 2000);
      }
    } catch (e) {
      console.error(e);
      button.textContent = 'Error';
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}
